import React, {useEffect, useState} from 'react';
import Table from '../../../components/common/table/Table';
import {Column} from "../../common/table/TableSection";
import DateRangeFilter from '../../common/table/DateRangeFilter';
import {useNavigate} from "react-router-dom";
import styles from "./AdminDocumentTable.module.css";
import {DocumentTableResponseData} from "../../../types/client";
import {DocumentSort, DocumentTarget, DocumentType} from "../../../types/document";
import {adminDocumentTableAPI, deleteDocumentsAPI, getClientListAPI} from "../../../services/admin";
import {ClientListResponseData} from "../../../types/admin";
import Button from "../../common/button/Button";
import {useAlert} from "../../common/error/AlertContext";

interface DateRange {
    start: Date | null;
    end: Date | null;
}

interface DateRanges {
    create: DateRange;
}

interface ExtendedColumn<T> extends Column<T> {
    render?: (value: any, row: T, index: number) => React.ReactNode;
}

//어드민 문서 테이블
const AdminDocumentTable = () => {
    const {showAlert} = useAlert();
    const {showConfirm} = useAlert();
    const [isClientListLoaded, setIsClientListLoaded] = useState(false);
    const navigate = useNavigate();
    const [data, setData] = useState<DocumentTableResponseData[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [pendingSearchTerm, setPendingSearchTerm] = useState('');
    const [searchType, setSearchType] = useState<DocumentTarget>(DocumentTarget.ALL);
    const [clientList, setClientList] = useState<ClientListResponseData[]>([]);
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const [filters, setFilters] = useState<Record<string, any>>({
        type: ['보고서', '견적서', '가공가이드라인'],
        clientIds: [],
        create: 'ALL'
    });

    //날짜 필터 값
    const [dateRanges, setDateRanges] = useState<DateRanges>({create: {start: null, end: null}});

    //정렬
    const [sort, setSort] = useState<{ field: string | null, direction: 'asc' | 'desc' | null }>({
        field: 'createdAt', direction: 'desc'
    });

    useEffect(() => {
        const checkboxes = document.querySelectorAll('input[type="checkbox"]');
        const headerCheckbox = checkboxes[0] as HTMLInputElement;
        if (headerCheckbox) {
            headerCheckbox.indeterminate = selectedRows.size > 0 && selectedRows.size < data.length;
        }
    }, [selectedRows, data]);

    const resetAllFilters = () => {
        // 필터 초기화
        setFilters({
            type: ['보고서', '견적서', '가공가이드라인'],
            clientIds: clientList.length > 0 ? clientList.map(client => client.clientId) : [],
            create: 'ALL'
        });

        // 날짜 범위 초기화
        setDateRanges({
            create: { start: null, end: null }
        });

        // 검색 관련 초기화
        setSearchTerm('');
        setPendingSearchTerm('');
        setSearchType(DocumentTarget.ALL);

        // 페이지 초기화
        setPage(1);

        // 정렬 초기화
        setSort({
            field: 'createdAt',
            direction: 'desc'
        });

        // 선택된 행 초기화
        setSelectedRows(new Set());
    };

    //체크박스 전체 선택
    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const allIds = data.map(row => row.documentId);
            setSelectedRows(new Set(allIds));
        } else {
            setSelectedRows(new Set());
        }
    };

    //선택된 문서 삭제
    const handleDelete = async () => {
        if (selectedRows.size === 0) {
            //선택된 문서 없는 경우
            showAlert('삭제할 항목을 선택해주세요.', "error");
            return;
        }
        showConfirm(
            //확인창
            '선택한 항목을 삭제하시겠습니까?',
            async () => {
                try {
                    //문서 삭제 API
                    await deleteDocumentsAPI(selectedRows);
                    showAlert('선택한 항목이 삭제되었습니다.', "info");
                    fetchData();
                    //체크박스 초기화
                    setSelectedRows(new Set());
                } catch (error) {
                    console.error('Delete failed:', error);
                }
            }
        )
    };

    //문서 생성 페이지
    const handleAdd = () => {
        navigate("/admin/documents/create");
    }

    //체크박스 단일 선택
    const handleSelectRow = (clientId: number, checked: boolean) => {
        const newSelected = new Set(selectedRows);
        if (checked) {
            newSelected.add(clientId);
        } else {
            newSelected.delete(clientId);
        }
        setSelectedRows(newSelected);
    };

    //클라이언트 목록 조회, 문서 공개 대상 클라이언트 선택용
    const fetchClientList = async () => {
        try {
            //클라이언트 목록 조회 API
            const response = await getClientListAPI();
            if (Array.isArray(response) && response.length > 0) {
                setClientList(response);
                setFilters(prev => ({
                    ...prev,
                    clientIds: response.map(client => client.clientId)
                }));
            }
            setIsClientListLoaded(true);
        } catch (error) {
            console.error('Error fetching client list:', error);
        }
    };

    useEffect(() => {
        fetchClientList();
    }, []);

    //테이블 로우 선택 시
    const handleRowClick = (row: DocumentTableResponseData) => {
        //세부 페이지로 이동
        navigate(`/admin/documents/${row.documentId}`);
    };

    //컬럼
    const columns: ExtendedColumn<DocumentTableResponseData>[] = [
        //체크박스
        {
            key: 'checkbox',
            header: (
                <input
                    type="checkbox"
                    checked={data.length > 0 && selectedRows.size === data.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={styles.checkbox}
                />
            ),
            width: '40px',
            render: (_, row) => (
                <input
                    type="checkbox"
                    checked={selectedRows.has(row.documentId)}
                    onChange={(e) => handleSelectRow(row.documentId, e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    className={styles.checkbox}
                />
            )
        },
        {
            key: 'documentId',
            header: 'No.',
            width: '10%',
            render: (value, row, index) => ((page - 1) * 7) + index + 1
        },
        {
            key: 'title',
            header: '제목',
            width: '60%'
        },
        {
            key: 'createdAt',
            header: '작성일',
            width: '30%',
            sortable: true,
            render: (value) => {
                const date = new Date(value);
                return `${date.getFullYear().toString().slice(2)}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} (${['일', '월', '화', '수', '목', '금', '토'][date.getDay()]})`;
            }
        }
    ];

    //보고서 종류 필터
    const typeFilterOptions = [
        {label: '보고서', value: '보고서'},
        {label: '견적서', value: '견적서'},
        {label: '가공가이드라인', value: '가공가이드라인'}
    ];

    //클라이언트 출력 변경, 클라이언트 식별자 > 클라이언트 회사명
    const clientDisplayMap = clientList.reduce((acc, client) => {
        acc[String(client.clientId)] = client.companyName;
        return acc;
    }, {} as Record<string, string>);

    //클라이언트 회사명, 식별자 매핑
    const clientFilterOptions = clientList.map(client => ({
        label: client.companyName, value: String(client.clientId)
    }));

    //API 사용 위한 날짜 데이터 형식 변환
    const formatDateForAPI = (date: Date | string | null): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    //API 사용 위한 날짜 범위 데이터 형식 변환
    const formatDateRangeForAPI = (start: Date | null, end: Date | null): string | null => {
        //선택 안한 경우 'ALL'로 표시
        if (!start && !end) return 'ALL';
        if (!end) return `${formatDateForAPI(start)}~`;
        //시작날짜~종료날짜 형식으로 변환
        return `${formatDateForAPI(start)}~${formatDateForAPI(end)}`;
    };

    //날짜 형식 변환
    const handleDateRangeChange = (key: string, start: Date | null, end: Date | null) => {
        setDateRanges(prev => ({
            ...prev,
            [key]: {start, end}
        }));

        const dateRangeString = formatDateRangeForAPI(start, end);
        setFilters(prev => ({
            ...prev,
            [key]: dateRangeString
        }));

        setPage(1);
    };

    //테이블 필터 설정
    const tableFilters = [
        {
            key: 'type',
            label: '문서 종류',
            options: typeFilterOptions,
            multiple: true,
            value: filters.type,
            component: undefined
        },
        {
            key: 'create',
            label: '작성일',
            component: (
                <DateRangeFilter
                    label="작성일"
                    startDate={dateRanges.create.start}
                    endDate={dateRanges.create.end}
                    onDateChange={(start, end) => handleDateRangeChange('create', start, end)}
                    isOpen={false}
                    onToggle={() => {
                    }}
                />
            ),
            options: [],
            multiple: false,
            value: null
        },
        {
            key: 'clientIds',
            label: '공개 대상 클라이언트',
            options: clientFilterOptions,
            multiple: true,
            value: filters.clientIds,
            component: undefined,
            displayMap: clientDisplayMap
        }
    ];

    //정렬 기준 설정
    const getSortParam = (field: string | null, direction: 'asc' | 'desc' | null): DocumentSort => {
        if (!field || !direction) return DocumentSort.CREATE_DESC;
        const sortMap: Record<string, Record<string, DocumentSort>> = {
            createdAt: {
                asc: DocumentSort.CREATE_ASC,
                desc: DocumentSort.CREATE_DESC
            }
        };
        return sortMap[field]?.[direction] || DocumentSort.CREATE_DESC;
    }

    //검색
    const handleSearch = () => {
        setSearchTerm(pendingSearchTerm);
        setPage(1);
    };

    //엔터키 사용 검색
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    //테이블 데이터 설정
    const fetchData = async () => {
        try {
            setLoading(true);
            //문서 데이터 API 호출
            const response = await adminDocumentTableAPI({
                page: page - 1,
                sort: getSortParam(sort.field, sort.direction),
                target: searchType,
                search: searchTerm,
                type: filters.type as DocumentType[],
                clientIds: filters.clientIds,
                create: filters.create
            });
            //데이터 설정
            setData(response.content);
            //페이지 설정
            setTotalPages(Math.ceil(response.total / 7));
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, searchTerm, searchType, sort, filters]);

    //검색 대상 설정
    const searchOptions = [
        {value: DocumentTarget.ALL, label: '전체'},
        {value: DocumentTarget.TITLE, label: '제목'},
        {value: DocumentTarget.CONTENT, label: '내용'}
    ];

    //필터값 변경
    const handleFilterChange = (key: string, value: unknown): void => {
        setFilters(prev => {
            if (key === 'type') {
                const newTypes = Array.isArray(value) ? Array.from(new Set(value)) : [];
                if (newTypes.length === 0) {
                    return {...prev, [key]: [DocumentType.보고서, DocumentType.견적서, DocumentType.가공가이드라인]};
                }
                return {...prev, [key]: newTypes};
            }

            if (key === 'clientIds') {
                let newClientIds: number[] = [];
                if (Array.isArray(value)) {
                    newClientIds = Array.from(new Set(value.map(id =>
                        typeof id === 'string' ? parseInt(id) : id
                    ).filter(id => !isNaN(id))));
                } else if (typeof value === 'string') {
                    newClientIds = Array.from(new Set(value
                        .split(',')
                        .filter(Boolean)
                        .map(id => parseInt(id))
                        .filter(id => !isNaN(id))));
                }
                return {...prev, clientIds: newClientIds};
            }
            return {...prev, [key]: value};
        });
        setPage(1);
    };

    //정렬 변경
    const handleSortChange = (field: string | null, direction: 'asc' | 'desc' | null) => {
        setSort({field, direction});
    };

    return (
        <div className={styles.container}>
            <div className={styles.searchContainer}>
                <div className={styles.titleContainer}>
                    <div className={styles.padding}/>
                    <h3>문서</h3>
                    <div className={styles.mobileEditDeleteButtons}>
                        {/*삭제 버튼*/}
                        <Button
                            variant="secondary"
                            onClick={handleDelete}
                            size={"small"}
                        >
                            삭제
                        </Button>
                        {/*생성 버튼*/}
                        <Button
                            variant="primary"
                            onClick={handleAdd}
                            size={"small"}
                        >
                            추가
                        </Button>
                    </div>
                </div>
                <div className={styles.searchInputContainer}>
                    <div>
                        {/*검색 대상 선택*/}
                        <select
                            value={searchType}
                            onChange={(e) => setSearchType(e.target.value as DocumentTarget)}
                            className={styles.searchTypeSelect}
                        >
                            {searchOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.searchInputWrapper}>
                        {/*검색 창*/}
                        <input
                            type="text"
                            placeholder="검색어를 입력하세요"
                            value={pendingSearchTerm}
                            onChange={(e) => setPendingSearchTerm(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className={styles.searchInput}
                        />
                        {/*검색 버튼*/}
                        <Button
                            onClick={handleSearch}
                            className={styles.searchButton}
                            variant="primary"
                            size={"small"}
                        >
                            검색
                        </Button>
                    </div>
                    <div className={styles.editDeleteButtons}>
                        {/*삭제 버튼*/}
                        <Button
                            variant="secondary"
                            onClick={handleDelete}
                            size={"small"}
                        >
                            삭제
                        </Button>
                        {/*생성 버튼*/}
                        <Button
                            variant="primary"
                            onClick={handleAdd}
                            size={"small"}
                        >
                            추가
                        </Button>
                    </div>
                </div>
            </div>
            {/*테이블*/}
            {isClientListLoaded && (
                <Table
                    columns={columns}
                    data={data}
                    loading={loading}
                    size="medium"
                    filters={tableFilters}
                    showPagination={true}
                    page={page}
                    pageSize={7}
                    totalItems={totalPages * 7}
                    onPageChange={setPage}
                    onFilterChange={handleFilterChange}
                    onSortChange={handleSortChange}
                    onRowClick={handleRowClick}
                    onReset={resetAllFilters}
                />
            )}
        </div>
    );
};

export default AdminDocumentTable;